import { useState } from "react";
import { Settings } from "lucide-react";
import { useTran, useUnsavedChanges } from "@/hooks";
import { PageHeader, UnsavedChangesDialog } from "@/components/common";
import ProfileSection from "../components/ProfileSection";
import PasswordSection from "../components/PasswordSection";
import { useSettingsProfileForm } from "../hooks/useSettingsProfileForm";
import { usePasswordForm } from "../hooks/usePasswordForm";
import { User, KeyRound } from "lucide-react";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const SectionSwitcher = ({ tabs, value, onChange }) => (
  <div className="flex flex-col gap-1 rounded-xl border border-border bg-card p-1 text-sm font-semibold text-muted-foreground sm:flex-row xl:flex-col">
    {tabs.map((tab) => {
      const isActive = value === tab.key;
      const Icon = tab.icon;
      return (
        <button
          key={tab.key}
          type="button"
          className={`flex w-full items-center justify-start gap-3 rounded-xl px-4 py-2 text-sm font-semibold transition ${
            isActive ? "bg-primary/10 text-primary" : "hover:bg-muted/60"
          }`}
          onClick={() => onChange(tab.key)}
        >
          <Icon className="h-4 w-4" />
          <span className="">{tab.label}</span>
        </button>
      );
    })}
  </div>
);

const SettingsPage = () => {
  const { t } = useTran();
  const [activeTab, setActiveTab] = useState("profile");
  const [isDirty, setIsDirty] = useState(false);
  const { showDialog, confirmDiscard, cancelDiscard, guardAction } =
    useUnsavedChanges(isDirty);
  const {
    initialProfileValues,
    handleProfileSubmit,
    uniqueUsernameValidator,
    uniqueEmailValidator,
  } = useSettingsProfileForm();
  const { toggles, handlePasswordSubmit } = usePasswordForm();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  const handleTabChange = (tab) => {
    guardAction(() => {
      setIsDirty(false);
      setActiveTab(tab);
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("User Settings")}
        description={t("settings.description")}
        icon={Settings}
      />
      <div className="grid gap-4 xl:grid-cols-[240px_1fr]">
        <Can
          resource="user_profile"
          action="view"
          defaultView={true}
          userPermissions={permissions}
          showMessage={true}
        >
          <div className="min-w-0">
            <SectionSwitcher
              value={activeTab}
              onChange={handleTabChange}
              tabs={[
                {
                  key: "profile",
                  label: t("settings.userInformation"),
                  icon: User,
                },
                {
                  key: "password",
                  label: t("settings.passwordUpdate"),
                  icon: KeyRound,
                },
              ]}
            ></SectionSwitcher>
          </div>
        </Can>
        <Can
          resource="user_profile"
          action="view"
          defaultView={true}
          userPermissions={permissions}
          showMessage={true}
        >
          <div className="min-w-0">
            <div className="grid gap-6 items-stretch">
              {activeTab === "profile" ? (
                <ProfileSection
                  initialValues={initialProfileValues}
                  onSubmit={handleProfileSubmit}
                  uniqueUsernameValidator={uniqueUsernameValidator}
                  uniqueEmailValidator={uniqueEmailValidator}
                  onDirtyChange={setIsDirty}
                />
              ) : (
                <PasswordSection
                  onSubmit={handlePasswordSubmit}
                  toggles={toggles}
                  onDirtyChange={setIsDirty}
                />
              )}
            </div>
          </div>
        </Can>
      </div>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </div>
  );
};

export default SettingsPage;
